import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {copyToClipboard} from 'nutshell-core/utils/copy-to-clipboard';
import {makeDataDangerous} from 'nutshell-core/utils/graphql-errors';

import {EditionThumbnailList} from 'shells/edition-thumbnail-list/edition-thumbnail-list';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from 'shells/icon/svg/external-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {UppercaseLabel} from 'shells/typography/uppercase-label';

import type {
    GetRecentEditions as GetRecentEditionsQuery,
    GetRecentEditionsVariables as GetRecentEditionsQueryVariables,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';

import GET_RECENT_EDITIONS_QUERY from '../editions/graphql/queries/get-recent-editions.graphql';
import {BroadcastMutations} from '../graphql-wrappers/broadcast/broadcast-mutations';
import {EditionMutations} from '../graphql-wrappers/edition/edition-mutations';

const THUMBNAIL_WIDTH = 316;

type OwnProps = {
    onCreateNewBroadcast: () => void;
};

type Props = OwnProps & {
    onDeleteEdition: (id: string, campaignId: string) => Promise<any>;
    onDeleteBroadcast: (id: string) => Promise<any>;
};

function RecentlyUpdatedComponent(props: Props) {
    const {data, loading, refetch} = useQuery<
        GetRecentEditionsQuery,
        GetRecentEditionsQueryVariables
    >(GET_RECENT_EDITIONS_QUERY, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
        variables: {
            limit: 5,
        },
    });
    const {user} = useGetSessionUser();

    const canAccessFullCampaigns =
        user && user.permissions && user.permissions.canAccessFullCampaigns;

    const {recentEditions} = makeDataDangerous(data);

    return (
        <div className='overflow-auto'>
            <div
                style={{
                    // The min width that will fit all the cards we want to show (minus 16px not on last element)
                    minWidth: recentEditions
                        ? `${THUMBNAIL_WIDTH * recentEditions.length - 16}px`
                        : `${THUMBNAIL_WIDTH * 4}px`, // loading state needs space of 4
                    // Add some padding so the scroll bar doesn't cover content
                    paddingBottom: '12px',
                }}
            >
                <div className='mb-8'>
                    <UppercaseLabel>Recently Updated</UppercaseLabel>
                </div>
                <EditionThumbnailList
                    editions={
                        recentEditions
                            ? // @ts-expect-error
                              recentEditions.map((edition) => {
                                  const options = [
                                      {
                                          label: 'View',
                                          handleSelect: () => {
                                              appHistory.push(edition.htmlUrlPath);
                                          },
                                          icon: <EyeIcon wrapWithDiv={false} size={18} />,
                                      },
                                  ];

                                  if (edition.campaign.type === 'BROADCAST') {
                                      options.push({
                                          label: 'Copy shareable URL',
                                          handleSelect: () => {
                                              copyToClipboard(edition.viewInBrowserLink);
                                          },
                                          // @ts-expect-error
                                          tooltipTextOnClose: 'Copied!',
                                          icon: <ExternalLinkIcon size={16} />,
                                      });
                                      options.push({
                                          label: 'Archive',
                                          // @ts-expect-error
                                          variant: 'destructive',
                                          handleSelect: () => {
                                              props
                                                  .onDeleteBroadcast(edition.campaign.id)
                                                  .then(() => {
                                                      refetch();
                                                  });
                                          },
                                          icon: <ArchiveIcon size={16} />,
                                      });
                                  } else if (edition.campaign.type === 'NEWSLETTER') {
                                      options.push({
                                          label: 'Copy shareable URL',
                                          handleSelect: () => {
                                              copyToClipboard(edition.viewInBrowserLink);
                                          },
                                          // @ts-expect-error
                                          tooltipTextOnClose: 'Copied!',
                                          icon: <ExternalLinkIcon size={16} />,
                                      });
                                      options.push({
                                          label: 'Archive',
                                          // @ts-expect-error
                                          variant: 'destructive',
                                          handleSelect: () => {
                                              props
                                                  .onDeleteEdition(edition.id, edition.campaign.id)
                                                  .then(() => {
                                                      refetch();
                                                  });
                                          },
                                          icon: <ArchiveIcon size={16} />,
                                      });
                                  }

                                  return {
                                      id: edition.id,
                                      name: edition.name,
                                      thumbnailUrl:
                                          edition.emailTemplate &&
                                          edition.emailTemplate.thumbnailUrl
                                              ? edition.emailTemplate.thumbnailUrl
                                              : undefined,
                                      isABTesting: edition.isABTesting,
                                      createdTime: edition.createdTime,
                                      modifiedTime: edition.modifiedTime,
                                      sendTime: edition.sendTime,
                                      status: edition.status,
                                      campaignType: edition.campaign.type,
                                      options,
                                      backgroundColor: edition.backgroundColor,
                                      owner: canAccessFullCampaigns
                                          ? edition.campaign.owner
                                          : undefined,
                                  };
                              })
                            : []
                    }
                    isLoading={loading}
                    singleRow={true}
                    addNew={
                        recentEditions && recentEditions.length === 0
                            ? {
                                  text: 'Create new broadcast',
                                  onClick: props.onCreateNewBroadcast,
                              }
                            : undefined
                    }
                    onSelect={(id) => {
                        // @ts-expect-error
                        const selectedEdition = recentEditions.find((edition) => {
                            return edition.id === id;
                        });

                        if (selectedEdition) {
                            appHistory.push(selectedEdition.htmlUrlPath);
                        }
                    }}
                    displayIcon={true}
                    shouldTruncateName={true}
                />
            </div>
        </div>
    );
}

export function RecentlyUpdated(props: OwnProps) {
    return (
        <EditionMutations>
            {({onDelete: onDeleteEdition}) => (
                <BroadcastMutations>
                    {({onDelete: onDeleteBroadcast}) => (
                        <RecentlyUpdatedComponent
                            {...props}
                            onDeleteEdition={onDeleteEdition}
                            onDeleteBroadcast={onDeleteBroadcast}
                        />
                    )}
                </BroadcastMutations>
            )}
        </EditionMutations>
    );
}
